
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import useTranslation from "next-translate/useTranslation";
import Link from "next/link";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { connect, ConnectedProps } from "react-redux";
import { Card, CardBody, CardHeader, Col, Collapse, Row, Spinner } from "reactstrap";
import { AnyAction, Dispatch } from "redux";
import { IProject, IProjectMembership, IProjectReport, ProjectProgress, ProjectState, UserRole } from "@api/schema";
import BaseLayout from "@components/BaseLayout";
import Icon from "@components/common/Icon";
import PageError from "@components/common/PageError";
import ErrorPage from "@components/ErrorPage";
import { withAuth } from "@components/hoc/withAuth";
import EmptyProjectCard from "@components/marketplace/projects/EmptyProjectCard";
import ProjectCard from "@components/marketplace/projects/ProjectCard";
import MyProjectCard from "@components/project/MyProjectCard";
import MyFollowershipsCard from "@components/usecases/my-projects/MyFollowershipsCard";
import { addNotificationAction } from "@redux/actions/notifications";
import { deleteModelAction, IFormikActions, newLoadCollectionAction, newLoadCollectionPageAction, updateModelAction } from "@redux/helper/actions";
import { AppState } from "@redux/reducer";
import { selectCurrentUser, selectIsAuthenticated } from "@redux/reducer/auth";
import { selectCollectionUsecaseState } from "@redux/reducer/data";
import { selectMyMemberships } from "@redux/reducer/myProjects";
import { EntityType, ScopeTypes } from "@redux/reduxTypes";
import { reportProjectAction } from "@redux/usecases/marketplace";
import { selectLoadingCurrentUserUsecaseState } from "@redux/usecases/userAccount";
import { Routes } from "@services/routes";
import { idFromIModelOrIRI, uriHashMatchesId } from "@services/util";
import { MODULE_CHALLENGE_AVAILABLE, MODULE_FUNDS_AVAILABLE } from "config";
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    addNotification: (message: string) => dispatch(addNotificationAction(message, "success")),
    deleteMembership: (membership: IProjectMembership, actions: IFormikActions) => dispatch(deleteModelAction(EntityType.ProjectMembership, membership, actions)),
    deleteProject: (project: IProject, actions: IFormikActions) => dispatch(deleteModelAction(EntityType.Project, project, actions)),
    // loadMyProjects: () => dispatch(loadMyProjectsAction()),
    loadMyProjects: () => dispatch(newLoadCollectionAction(EntityType.Project, null, ScopeTypes.MyProjects)),
    loadMyProjectsPage: (nextlink: string) => dispatch(newLoadCollectionPageAction(EntityType.Project, nextlink, ScopeTypes.MyProjects)),
    reportProject: (project: IProject, report: IProjectReport, actions: IFormikActions) => dispatch(reportProjectAction(project, report, actions)),
    updateProject: (data: IProject, actions: IFormikActions) => dispatch(updateModelAction(EntityType.Project, data, actions)), // NOTE / @todo projectRequest-state is never used to feedback loading/error
});
const mapStateToProps = (state: AppState) => ({
    myMemberships: selectMyMemberships(state),
    myProjectsState: selectCollectionUsecaseState(state, EntityType.Project, ScopeTypes.MyProjects),
    isAuthenticated: selectIsAuthenticated(state),
    currentUser: selectCurrentUser(state),
    userRequest: selectLoadingCurrentUserUsecaseState(state),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
type PageProps = ConnectedProps<typeof connector>;
// that ID is used to identify, the element in the index page of the project dash board, which shows the ideas
export const ideaListCardHashId = "IdeaList";
/**
 * This page shows a dashboard of the projects of the user
 */
const MyProjectsPage: React.FC<PageProps> = (props: PageProps) => {
    const { currentUser, myMemberships, myProjectsState, userRequest } = props;
    const { t } = useTranslation("common");
    useEffect(() => {
        // the currentUser could be missing while myProjectsState.loaded is still true, e.g after he
        // was included in a list of users -> force refresh
        if (!myProjectsState.loaded || !currentUser) {
            props.loadMyProjects();
        }
        else if (myProjectsState.itemIds.length === 0) {
            // after loading finished, e.g. when the last membership was removed
            setIdeasOpen(true);
        }
        // if just the currentUser or the property currentUser?.createdProjects is used in the dependency array,
        // the useEffect will run in an infinite loop
        // Why? Assumption: with every render the actual object (currentUser; currentUser.createdProjects) is recreated
        // Information about react comparison: if the value in the dependency array is a primitive, the comparison is by value,
        // if it's an object, the comparison is by reference,
        // therefor the reference could change and the useEffect will be called
        // Why it is enought to observe the length?
        // Cause, if a new created project appears, the length of the array will change and the useEffect will be called
        // if the user has no projects and the currentUser?.createdProjects?.length  in the dependency array is replaced with currentUser or currentUser?.createdProjects?,
        // the state myProjectsState.loaded will always be false and therefor the props.loadMyProjects will run again (infinite loop)
        // @todo question: if the currentUser?.createdProjects?.length  in the dependency array is replaced with currentUser or currentUser?.createdProjects?
        // and an user is logging-in, who has at least one project or an idea, the infinite loop will stop. Why?
        // if you log !myProjectsState.loaded and !currentUser you will see the same values several times, which is a hint for the loop,
        // cause an useEffect will only run, if a value is changed or the reference of an object.
        // Assumption: the reference of the object (currentUser or currentUser?.createdProjects) has stopped to change. Why?
    }, [myProjectsState.loaded, currentUser?.createdProjects?.length, currentUser?.projectMemberships.length]);
    const myIdeas = currentUser?.createdProjects?.filter(p => p.progress === ProjectProgress.Idea);
    const noProjects: boolean = myProjectsState.loaded && myProjectsState.itemIds.length === 0;
    const noIdeas: boolean = myProjectsState.loaded && myIdeas?.length === 0;
    const getActiveProposal = (project: IProject) => project?.proposals?.filter(p => p.active).shift();
    const initialOpenCardIdeaList = uriHashMatchesId(ideaListCardHashId);
    const [ideasOpen, setIdeasOpen] = useState(noProjects);
    const toggleIdeas = () => { setIdeasOpen(!ideasOpen); };
    const allMyProjects = myProjectsState?.getItems<IProject>();
    const allMyFollowerships = currentUser?.followerships;
    // #region loading errors
    // my projects may be empty, so check for loadingError only
    if (myProjectsState?.loadingError) {
        return <ErrorPage statusCode={404} error={myProjectsState?.loadingError}/>;
    }
    if ((userRequest?.loaded && !currentUser) || userRequest?.loadingError) {
        return <ErrorPage statusCode={404} error={userRequest?.loadingError || "user.currentUserNotLoaded"}/>;
    }
    // #endregion
    return (<BaseLayout pageTitle={t("page.user.projects.title")}>
      <Row>
        <Col>
          <PageError error={myProjectsState?.loadingError}/>

          {noProjects && noIdeas && <Link href={Routes.Marketplace}>
            <a>
              <EmptyProjectCard />
            </a>
          </Link>}

          {myProjectsState.loaded && allMyProjects?.length > 0 && <InfiniteScroll data-testid="ProjectScroller" dataLength={allMyProjects.length} next={() => { props.loadMyProjectsPage(myProjectsState.nextLink); }} hasMore={!!myProjectsState.nextLink} loader={null} scrollableTarget="scrollable-page">
            {// order: active - inactive - deactivated
            allMyProjects.sort((a, b) => a.state === b.state ? 0 : a.state === ProjectState.Active ? -1 : a.state === ProjectState.Deactivated ? 1 : 0).map((project) => <MyProjectCard addNotification={props.addNotification} deleteMembership={props.deleteMembership} deleteProject={props.deleteProject} updateProject={props.updateProject} isSingle={allMyProjects.length === 1} key={project.id} project={project} membership={myMemberships && myMemberships.find((m) => idFromIModelOrIRI(m.project) === project.id)} activeProposal={(MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) ? getActiveProposal(project) : undefined}/>)}
          </InfiniteScroll>}

          {myProjectsState.isLoading && <Spinner />}
        </Col>
      </Row>

      {allMyFollowerships?.length > 0 && <Row className="mt-5">
        <Col>
          <MyFollowershipsCard />
        </Col>
      </Row>}

      {myIdeas && myIdeas.length > 0 && <Row className="mt-5">
        <Col>
          <Card className="collapsable-card" data-testid="CreatedIdeasCard">
            <CardHeader id="toggler-ideas" onClick={toggleIdeas} aria-expanded={ideasOpen ? "true" : "false"}><div className="title-section--with-element">
                <div className="title-section">
                  <h2>{t("page.user.projects.createdIdeas")}</h2>
                </div>
                <Link href={Routes.CreateIdea}>
                  {/* added onClick to prevent opening/closing the card, when clicking on the link */}
                  <a className="btn primary-btn" onClick={(e) => e.stopPropagation()}>
                    {t("page.user.projects.addIdea")}
                  </a>
                </Link>
              </div>
              <span className={"chevron-right" + (ideasOpen ? " chevron-right--toggled" : "")}><Icon name="chevron-right" size={42}/></span>
            </CardHeader>

            <Collapse id={ideaListCardHashId} isOpen={ideasOpen || initialOpenCardIdeaList}>
              <CardBody>
                <Row><p>{t("page.user.projects.takeIdea")}</p></Row>
                <Row className="card-grid-layout">
                  {myIdeas.map((idea) => (<Col key={idea.id}>
                      <ProjectCard project={idea}/>
                    </Col>))}
                </Row>
              </CardBody>
            </Collapse>
          </Card>
        </Col>
      </Row>}
    </BaseLayout>);
};
const __Next_Translate__Page__18c4f517a38__ = withAuth(connector(MyProjectsPage), UserRole.User);

    export default __appWithI18n(__Next_Translate__Page__18c4f517a38__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: false,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  